<template>
  <div class="formulario-modelo">

    <!-- Cabecera -->
    <div class="header-flex">
      <div>
        <h1>Informe de autoevaluación</h1>
        <p v-if="nombreVersion" class="ctx-modelo">{{ nombreVersion }}</p>
        <p v-if="informe" class="subtitulo">{{ informe.titulo }}</p>
      </div>
      <button class="btn salir" @click="$router.push({ name: 'ProcesoAutoevaluacionUsuario' })">Volver</button>
    </div>
    <div class="divider"></div>

    <!-- Cargando -->
    <div v-if="cargando" class="info">Cargando informe…</div>

    <!-- ═══ SIN INFORME ═══ -->
    <div v-else-if="!informe" class="selector-tipo">
      <p class="ayuda">
        {{ tipoAutoDetectado
          ? 'Tipo de informe detectado según el proceso. Genere la estructura para continuar.'
          : 'Esta ejecución aún no tiene informe. Seleccione el tipo para generar su estructura:' }}
      </p>
      <div class="tipos">
        <label
          v-for="t in tipos"
          :key="t.id_tipo_informe"
          class="tipo-card"
          :class="{
            activo: tipoSeleccionado === t.id_tipo_informe,
            'tipo-card--oculto': tipoAutoDetectado && tipoSeleccionado !== t.id_tipo_informe
          }"
        >
          <input
            v-model="tipoSeleccionado"
            type="radio"
            :value="t.id_tipo_informe"
            :disabled="tipoAutoDetectado"
          />
          <div>
            <span class="tipo-nombre">{{ t.etiqueta }}</span>
            <span class="tipo-detalle">Estructura {{ t.estructura }} · {{ t.secciones_default.length }} secciones</span>
          </div>
        </label>
      </div>
      <div class="botones-generar">
        <button class="btn guardar" :disabled="!tipoSeleccionado || generando" @click="generar">
          {{ generando ? 'Generando…' : 'Generar estructura del informe' }}
        </button>
      </div>
    </div>

    <!-- ═══ CON INFORME ═══ -->
    <template v-else>

      <!-- Barra de estado -->
      <div class="barra-estado">
        <div class="barra-izq">
          <span class="badge-estado" :class="informe.estado">{{ etiquetaEstado }}</span>
          <span class="progreso-texto">
            Redacción: <strong>{{ redactados }}/{{ obligatorios }}</strong> bloques obligatorios
          </span>
        </div>
        <div class="barra-der">
          <button class="btn btn-toggle-arbol" @click="arbolVisible = !arbolVisible">
            {{ arbolVisible ? '◀ Ocultar menú' : '▶ Mostrar menú' }}
          </button>
          <button class="btn accion" @click="mostrarPreview = true">Ver informe</button>
          <button class="btn salir" @click="descargarWord">Descargar Word</button>
          <button class="btn salir" @click="descargarPdf">Descargar PDF</button>
          <button
            v-if="!avalado"
            class="btn guardar"
            :disabled="redactados < obligatorios || avalando"
            @click="avalar"
          >
            {{ avalando ? 'Avalando…' : 'Avalar informe' }}
          </button>
        </div>
      </div>

      <!-- Panel doble: árbol + editor -->
      <div class="panel-doble" :class="{ 'panel-sin-arbol': !arbolVisible }">

        <!-- ── Árbol de navegación ── -->
        <aside v-if="arbolVisible" class="arbol">

          <!-- Encabezado del árbol -->
          <div class="arbol-head">
            <span class="arbol-head-label">Secciones</span>
            <button
              class="btn-modo-edicion"
              :class="{ activo: modoEdicionArbol }"
              @click="modoEdicionArbol = !modoEdicionArbol"
            >
              {{ modoEdicionArbol ? 'Listo' : 'Editar' }}
            </button>
          </div>

          <!-- ── MODO NORMAL ── -->
          <template v-if="!modoEdicionArbol">
            <div
              v-for="s in seccionesVisibles"
              :key="s.id"
              class="nodo"
              :class="[s.tipo === 'libre' ? 'libre' : 'general', { activo: esSeccionActiva(s) }]"
              @click="seleccionarSeccion(s)"
            >
              <span class="check">{{ tieneContenidoSeccion(s) ? '✓' : '○' }}</span>
              {{ s.titulo }}
            </div>

            <div class="arbol-separador">Factores y características</div>

            <template v-for="f in factores" :key="f.id">
              <div
                class="nodo factor"
                :class="{ activo: seleccion?.tipo === 'componente' && seleccion.id === f.id && seleccion.nivel === 'factor' }"
                @click="seleccionarComponente(f.id, 'factor')"
              >
                <span class="check">{{ factorCompleto(f.id) ? '✓' : '○' }}</span>
                {{ f.nombre }}
              </div>
              <div
                v-for="c in f.hijos"
                :key="c.id"
                class="nodo caracteristica"
                :class="{ activo: seleccion?.tipo === 'componente' && seleccion.id === c.id }"
                @click="seleccionarComponente(c.id, 'caracteristica')"
              >
                <span class="check">{{ redaccion(c.id, 'analisis')?.contenido ? '✓' : '○' }}</span>
                {{ c.nombre }}
              </div>
            </template>
          </template>

          <!-- ── MODO EDICIÓN ── -->
          <template v-else>

            <!-- Secciones generales -->
            <div class="edit-grupo-label">Secciones del informe</div>

            <div
              v-for="(s, idx) in estructura.secciones"
              :key="s.id"
              class="edit-item"
              :class="{ oculta: !s.visible }"
            >
              <button
                class="edit-toggle"
                :class="{ visible: s.visible }"
                @click="toggleVisibilidadSeccion(s.id)"
              >{{ s.visible ? 'Visible' : 'Oculta' }}</button>
              <span class="edit-titulo">{{ s.titulo }}</span>
              <div class="edit-ctrl">
                <button class="btn-ctrl" :disabled="idx === 0" @click="moverSeccion(s.id, -1)">▲</button>
                <button class="btn-ctrl" :disabled="idx === estructura.secciones.length - 1" @click="moverSeccion(s.id, 1)">▼</button>
                <button v-if="s.tipo === 'libre'" class="btn-ctrl eliminar" @click="eliminarSeccionLibre(s.id)">✕</button>
              </div>
            </div>

            <div class="nueva-sec-form">
              <input
                v-model="nuevaSeccionTitulo"
                class="form-control-mini"
                placeholder="Nueva sección…"
                @keyup.enter="agregarSeccionLibre"
              />
              <button class="btn-add-sec" :disabled="!nuevaSeccionTitulo.trim()" @click="agregarSeccionLibre">
                Añadir
              </button>
            </div>

            <div class="arbol-separador">Factores y características</div>

            <!-- Sub-secciones por factor -->
            <template v-for="f in factores" :key="f.id">
              <div class="edit-factor-label">{{ f.nombre }}</div>

              <div
                v-for="(sub, sidx) in subSeccionesDeFactorTodas(f.id)"
                :key="sub.clave"
                class="edit-item sub"
                :class="{ oculta: !sub.visible }"
              >
                <button
                  class="edit-toggle"
                  :class="{ visible: sub.visible }"
                  @click="toggleVisibilidadSubSeccion(f.id, sub.clave)"
                >{{ sub.visible ? 'Visible' : 'Oculta' }}</button>
                <span class="edit-titulo">{{ sub.titulo }}</span>
                <div class="edit-ctrl">
                  <button class="btn-ctrl" :disabled="sidx === 0" @click="moverSubSeccion(f.id, sub.clave, -1)">▲</button>
                  <button class="btn-ctrl" :disabled="sidx === subSeccionesDeFactorTodas(f.id).length - 1" @click="moverSubSeccion(f.id, sub.clave, 1)">▼</button>
                  <button v-if="sub.libre" class="btn-ctrl eliminar" @click="eliminarSubSeccionLibre(f.id, sub.clave)">✕</button>
                </div>
              </div>

              <div v-if="factorEditandoId === f.id" class="nueva-sec-form sub">
                <input
                  v-model="nuevaSubSeccionTitulo"
                  class="form-control-mini"
                  placeholder="Nombre de sub-sección…"
                  @keyup.enter="agregarSubSeccionLibre(f.id)"
                />
                <button class="btn-add-sec" :disabled="!nuevaSubSeccionTitulo.trim()" @click="agregarSubSeccionLibre(f.id)">Añadir</button>
                <button class="btn-cancel" @click="factorEditandoId = null">Cancelar</button>
              </div>
              <button
                v-else
                class="btn-add-subsec"
                @click="factorEditandoId = f.id; nuevaSubSeccionTitulo = ''"
              >+ Sub-sección</button>

            </template>
          </template>
        </aside>

        <!-- ── Panel editor ── -->
        <section class="editor-panel">
          <template v-if="seleccion">

            <!-- Tabs (solo factores, dinámicas) -->
            <div v-if="seleccion.tipo === 'componente' && seleccion.nivel === 'factor'" class="tabs">
              <button
                v-for="sub in subSeccionesDeFactorVisibles(seleccion.id)"
                :key="sub.clave"
                class="tab"
                :class="{ activo: bloqueActivo === sub.clave }"
                @click="cambiarBloque(sub.clave)"
              >{{ sub.titulo }}</button>
            </div>

            <!-- Contexto -->
            <p v-if="contexto" class="contexto">{{ contexto }}</p>

            <!-- Bloques de datos -->
            <div class="bloques-area">
              <div class="bloques-header">
                <span class="bloques-label">Bloques de datos</span>
                <button v-if="!avalado" class="btn-insertar" @click="mostrarCatalogo = true">
                  + Insertar
                </button>
              </div>
              <div v-if="bloquesActuales.length" class="bloques-lista">
                <div
                  v-for="(b, idx) in bloquesActuales"
                  :key="b.id"
                  class="bloque-item"
                  :class="{ 'bloque-editando': bloqueEditandoId === b.id }"
                >
                  <div class="bloque-controles">
                    <span class="bloque-tipo-label">{{ b.tipo === 'TEXTO' ? 'Texto libre' : b.tipo.replace(/_/g, ' ') }}</span>
                    <div class="bloque-acciones">
                      <button class="btn-ctrl" :disabled="idx === 0" @click="moverBloque(idx, -1)">▲</button>
                      <button class="btn-ctrl" :disabled="idx === bloquesActuales.length - 1" @click="moverBloque(idx, 1)">▼</button>
                      <button
                        v-if="b.tipo !== 'TEXTO'"
                        class="btn-ctrl btn-ctrl--texto"
                        @click="toggleColapsarBloque(b.id)"
                      >{{ bloquesColapsados.includes(b.id) ? 'Mostrar' : 'Ocultar' }}</button>
                      <button v-if="b.tipo === 'TEXTO' && !avalado && bloqueEditandoId !== b.id" class="btn-ctrl opciones" @click="editarBloqueTexto(b.id)">
                        Editar
                      </button>
                      <button v-else-if="b.tipo !== 'TEXTO'" class="btn-ctrl opciones" @click="bloqueConfigurando = b">Opciones</button>
                      <button v-if="!avalado" class="btn-ctrl eliminar" @click="eliminarBloqueConReset(b.id)">✕</button>
                    </div>
                  </div>

                  <!-- TEXTO: editor inline cuando se edita, contenido renderizado si no -->
                  <template v-if="b.tipo === 'TEXTO'">
                    <div v-if="bloqueEditandoId === b.id" class="bloque-editor-inline">
                      <div :ref="(el: any) => { inlineEditorEl = el }" class="editor"></div>
                      <div class="editor-pie">
                        <button class="btn guardar" :disabled="guardando" @click="guardar">
                          {{ guardando ? 'Guardando…' : 'Guardar' }}
                        </button>
                        <button class="btn salir" @click="cancelarEdicionBloque">Cancelar</button>
                      </div>
                    </div>
                    <div
                      v-else
                      class="bloque-texto-preview"
                      :class="{ 'bloque-texto-preview--editable': !avalado }"
                      @click="!avalado && editarBloqueTexto(b.id)"
                    >
                      <BloqueDatos :tipo="b.tipo" :params="b.params" />
                    </div>
                  </template>

                  <!-- Bloques de datos: colapsables -->
                  <template v-else>
                    <div v-if="bloquesColapsados.includes(b.id)" class="bloque-colapsado-label">
                      Tabla — {{ b.tipo.replace(/_/g, ' ') }}
                    </div>
                    <BloqueDatos v-else :tipo="b.tipo" :params="b.params" @update:vistaInicial="actualizarVistaComparativo(b.id, $event)" />
                  </template>
                </div>
              </div>
              <p v-else class="bloques-vacio">Sin bloques insertados en esta sección.</p>
            </div>

            <!-- Solo lectura -->
            <div v-if="avalado" class="solo-lectura" v-html="contenidoActual || '<p><i>Sin contenido</i></p>'"></div>

            <!-- Editor de texto de la sección (solo cuando no se edita un bloque inline) -->
            <template v-else-if="!bloqueEditandoId">
              <div class="editor-cabecera">
                <span class="editor-contexto-label">Texto de la sección</span>
              </div>
              <div ref="editorEl" class="editor"></div>
              <div class="editor-pie">
                <button class="btn guardar" :disabled="guardando" @click="guardar">
                  {{ guardando ? 'Guardando…' : 'Guardar' }}
                </button>
              </div>
            </template>
          </template>

          <div v-else class="vacio">
            <p>Seleccione una sección del panel izquierdo para redactar</p>
          </div>
        </section>

      </div>
    </template>

  </div>

  <CatalogoBloquesModal
    :visible="mostrarCatalogo"
    @cerrar="mostrarCatalogo = false"
    @insertar="agregarBloque"
  />

  <ConfiguradorBloqueModal
    :visible="!!bloqueConfigurando"
    :bloque="bloqueConfigurando"
    @cerrar="bloqueConfigurando = null"
    @guardar="actualizarParams"
  />

  <VistaPreviewInforme
    v-if="mostrarPreview && informe"
    :visible="mostrarPreview"
    :informe="informe!"
    :factores="factores"
    :auto-imprimir="imprimirAlAbrir"
    :auto-descargar-word="wordAlAbrir"
    @cerrar="mostrarPreview = false; imprimirAlAbrir = false; wordAlAbrir = false"
  />

  <MensajeFlotante :visible="msj.visible" :mensaje="msj.mensaje" :tipo="msj.tipo" @cerrar="msj.visible = false" />
</template>

<script setup lang="ts">
/**
 * Redacción del informe de autoevaluación.
 *
 * Editor enriquecido por componente y exportación a PDF. El estado `avalado` es
 * el que habilita la radicación.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/ejecucion/:id_ejecucion/informe` (InformeAutoevaluacion) — permiso `informes.ver`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, computed, reactive, onMounted, nextTick, watch } from 'vue'
import { useRoute } from 'vue-router'
import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import {
  getTiposInformeService, generarInformeService, getInformeService,
  actualizarInformeService, guardarRedaccionService, avalarInformeService,
  type Informe, type TipoInforme, type RedaccionInforme
} from '@/services/InformeService'
import { getProcesoByIdService, getTiposEjecucionModeloService } from '@/services/ProcesoAutoevaluacionService'
import { useGestionResultadosAutoevaluacion } from '@/store/GestionResultadosAutoevaluacion'
import BloqueDatos from '@/components/BloqueDatos.vue'
import CatalogoBloquesModal from '@/components/CatalogoBloquesModal.vue'
import ConfiguradorBloqueModal from '@/components/ConfiguradorBloqueModal.vue'
import VistaPreviewInforme from '@/components/VistaPreviewInforme.vue'
import MensajeFlotante from '@/components/MensajeFlotante.vue'

const route = useRoute()
const idEjecucion = Number(route.params.id_ejecucion)


interface BloqueDesc { id: string; tipo: string; params?: Record<string, any> }
interface NodoArbol { id: number; nombre: string; hijos: Array<{ id: number; nombre: string }> }

interface SeccionItem {
  id: string
  tipo: 'default' | 'libre'
  campo?: string
  titulo: string
  visible: boolean
}

interface SubSeccionItem {
  clave: string
  titulo: string
  visible: boolean
  libre: boolean
}

interface EstructuraInforme {
  secciones: SeccionItem[]
  factoresSubs: Record<string, SubSeccionItem[]>
}

type Seleccion =
  | { tipo: 'general'; campo: string }
  | { tipo: 'libre'; id: string }
  | { tipo: 'componente'; id: number; nivel: 'factor' | 'caracteristica' }

const SECCIONES_DEFAULT: SeccionItem[] = [
  { id: 'gen-presentacion',         tipo: 'default',      campo: 'presentacion',         titulo: 'Presentación',             visible: true },
  { id: 'gen-descripcion_programa', tipo: 'default',      campo: 'descripcion_programa', titulo: 'Descripción del programa', visible: true },
  { id: 'gen-analisis_resultados',  tipo: 'default',      campo: 'analisis_resultados',  titulo: 'Análisis de resultados',   visible: true },
  { id: 'gen-conclusion',           tipo: 'default',      campo: 'conclusion',           titulo: 'Conclusión',               visible: true },
]

const SUBSECCIONES_DEFAULT: SubSeccionItem[] = [
  { clave: 'conclusiones',             titulo: 'Conclusiones',          visible: true, libre: false },
  { clave: 'fortalezas_edit',          titulo: 'Fortalezas',            visible: true, libre: false },
  { clave: 'debilidades_edit',         titulo: 'Debilidades',           visible: true, libre: false },
  { clave: 'acciones_fortalecimiento', titulo: 'Acc. fortalecimiento',  visible: true, libre: false },
  { clave: 'acciones_mejoramiento',    titulo: 'Acc. mejoramiento',     visible: true, libre: false },
  { clave: 'anexos',                   titulo: 'Anexos',                visible: true, libre: false },
]

const resultadosStore = useGestionResultadosAutoevaluacion()
const mostrarCatalogo    = ref(false)
const mostrarPreview     = ref(false)
const imprimirAlAbrir    = ref(false)
const wordAlAbrir        = ref(false)
const bloqueConfigurando = ref<BloqueDesc | null>(null)
const bloquesActuales    = ref<BloqueDesc[]>([])
const bloquesColapsados  = ref<string[]>([])
const bloqueEditandoId   = ref<string | null>(null)
const cargando     = ref(true)
const generando    = ref(false)
const guardando    = ref(false)
const avalando     = ref(false)
const msj = reactive({ visible: false, mensaje: '', tipo: 'positivo' as 'positivo' | 'error' })
const tipos              = ref<TipoInforme[]>([])
const tipoSeleccionado   = ref<number | null>(null)
const tipoAutoDetectado  = ref(false)
const nombreVersion    = ref('')
const informe          = ref<Informe | null>(null)
const factores         = ref<NodoArbol[]>([])
const seleccion    = ref<Seleccion | null>(null)
const bloqueActivo = ref('conclusiones')
const editorEl     = ref<HTMLElement | null>(null)
let inlineEditorEl: HTMLElement | null = null
let quillMountedOn: HTMLElement | null = null
let quill: Quill | null = null

function toast(msg: string, tipo: 'positivo' | 'error' = 'positivo'): void {
  msj.mensaje = msg
  msj.tipo = tipo
  msj.visible = true
}

function toggleColapsarBloque(id: string): void {
  if (bloquesColapsados.value.includes(id)) {
    bloquesColapsados.value = bloquesColapsados.value.filter(x => x !== id)
  } else {
    bloquesColapsados.value = [...bloquesColapsados.value, id]
  }
}
const arbolVisible        = ref(true)
const modoEdicionArbol    = ref(false)
const estructura          = ref<EstructuraInforme>({ secciones: SECCIONES_DEFAULT.map(s => ({ ...s })), factoresSubs: {} })
const nuevaSeccionTitulo  = ref('')
const nuevaSubSeccionTitulo = ref('')
const factorEditandoId    = ref<number | null>(null)

function claveEstructura(): string {
  return `informe-estructura-${informe.value!.id_informe}`
}

function cargarEstructura(): void {
  if (!informe.value) return
  try {
    const raw = localStorage.getItem(claveEstructura())
    if (raw) {
      const parsed = JSON.parse(raw) as EstructuraInforme
      // Asegurar que las secciones por defecto existen si se añadieron nuevas en el código
      const ids = new Set(parsed.secciones.map(s => s.id))
      for (const def of SECCIONES_DEFAULT) {
        if (!ids.has(def.id)) parsed.secciones.push({ ...def })
      }
      estructura.value = parsed
    } else {
      estructura.value = { secciones: SECCIONES_DEFAULT.map(s => ({ ...s })), factoresSubs: {} }
    }
  } catch {
    estructura.value = { secciones: SECCIONES_DEFAULT.map(s => ({ ...s })), factoresSubs: {} }
  }
}

function persistirEstructura(): void {
  if (!informe.value) return
  localStorage.setItem(claveEstructura(), JSON.stringify(estructura.value))
}

// Computeds de estructura 

const seccionesVisibles = computed<SeccionItem[]>(() =>
  estructura.value.secciones.filter(s => s.visible)
)

function subSeccionesDeFactorTodas(factorId: number): SubSeccionItem[] {
  return estructura.value.factoresSubs[String(factorId)]
    ?? SUBSECCIONES_DEFAULT.map(s => ({ ...s }))
}

function subSeccionesDeFactorVisibles(factorId: number): SubSeccionItem[] {
  return subSeccionesDeFactorTodas(factorId).filter(s => s.visible)
}

// Operaciones de estructura 

function toggleVisibilidadSeccion(id: string): void {
  const s = estructura.value.secciones.find(x => x.id === id)
  if (s) { s.visible = !s.visible; persistirEstructura() }
}

function moverSeccion(id: string, dir: -1 | 1): void {
  const arr = estructura.value.secciones
  const idx = arr.findIndex(s => s.id === id)
  const nIdx = idx + dir
  if (nIdx < 0 || nIdx >= arr.length) return
  ;[arr[idx], arr[nIdx]] = [arr[nIdx], arr[idx]]
  persistirEstructura()
}

function agregarSeccionLibre(): void {
  const titulo = nuevaSeccionTitulo.value.trim()
  if (!titulo) return
  estructura.value.secciones.push({ id: `gen-libre-${crypto.randomUUID()}`, tipo: 'libre', titulo, visible: true })
  nuevaSeccionTitulo.value = ''
  persistirEstructura()
}

function eliminarSeccionLibre(id: string): void {
  estructura.value.secciones = estructura.value.secciones.filter(s => s.id !== id)
  if (seleccion.value?.tipo === 'libre' && (seleccion.value as any).id === id) seleccion.value = null
  persistirEstructura()
}

function toggleVisibilidadSubSeccion(factorId: number, clave: string): void {
  const subs = subSeccionesDeFactorTodas(factorId).map(s => ({ ...s }))
  const s = subs.find(x => x.clave === clave)
  if (s) {
    s.visible = !s.visible
    estructura.value.factoresSubs[String(factorId)] = subs
    persistirEstructura()
  }
}

function moverSubSeccion(factorId: number, clave: string, dir: -1 | 1): void {
  const subs = subSeccionesDeFactorTodas(factorId).map(s => ({ ...s }))
  const idx = subs.findIndex(s => s.clave === clave)
  const nIdx = idx + dir
  if (nIdx < 0 || nIdx >= subs.length) return
  ;[subs[idx], subs[nIdx]] = [subs[nIdx], subs[idx]]
  estructura.value.factoresSubs[String(factorId)] = subs
  persistirEstructura()
}

function agregarSubSeccionLibre(factorId: number): void {
  const titulo = nuevaSubSeccionTitulo.value.trim()
  if (!titulo) return
  const subs = subSeccionesDeFactorTodas(factorId).map(s => ({ ...s }))
  subs.push({ clave: `libre-${crypto.randomUUID()}`, titulo, visible: true, libre: true })
  estructura.value.factoresSubs[String(factorId)] = subs
  nuevaSubSeccionTitulo.value = ''
  factorEditandoId.value = null
  persistirEstructura()
}

function eliminarSubSeccionLibre(factorId: number, clave: string): void {
  const subs = subSeccionesDeFactorTodas(factorId).filter(s => s.clave !== clave)
  estructura.value.factoresSubs[String(factorId)] = subs
  persistirEstructura()
}

// Texto libre (localStorage) 

function claveTextoLibre(sectionId: string): string {
  return `informe-texto-libre-${informe.value!.id_informe}-${sectionId}`
}

function getTextoLibre(sectionId: string): string | null {
  try { return localStorage.getItem(claveTextoLibre(sectionId)) } catch { return null }
}

function setTextoLibre(sectionId: string, html: string | null): void {
  if (html) localStorage.setItem(claveTextoLibre(sectionId), html)
  else localStorage.removeItem(claveTextoLibre(sectionId))
}

function esBloqueLibre(factorId: number, clave: string): boolean {
  return subSeccionesDeFactorTodas(factorId).find(s => s.clave === clave)?.libre ?? false
}

function tieneContenidoLibre(id: string): boolean {
  if (!informe.value) return false
  try {
    if (localStorage.getItem(claveTextoLibre(id))) return true
    const raw = localStorage.getItem(storageKey(id))
    return raw ? (JSON.parse(raw) as any[]).length > 0 : false
  } catch { return false }
}

//  Selección e indicadores 

function esSeccionActiva(s: SeccionItem): boolean {
  const sel = seleccion.value
  if (!sel) return false
  if (s.tipo === 'default' && sel.tipo === 'general') return (sel as any).campo === s.campo
  if (s.tipo === 'libre' && sel.tipo === 'libre') return (sel as any).id === s.id
  return false
}

function tieneContenidoSeccion(s: SeccionItem): boolean {
  if (!informe.value) return false
  if (s.tipo === 'default' && s.campo) return !!(informe.value as any)[s.campo]
  return tieneContenidoLibre(s.id)
}

function seleccionarSeccion(s: SeccionItem): void {
  if (s.tipo === 'default' && s.campo) {
    seleccion.value = { tipo: 'general', campo: s.campo }
  } else {
    seleccion.value = { tipo: 'libre', id: s.id }
  }
  montarEditor()
}

//  Redacciones e indicadores de completitud 

const avalado = computed(() => informe.value?.estado === 'avalado')
const etiquetaEstado = computed(() =>
  ({ borrador: 'Borrador', en_revision: 'En revisión', avalado: 'Avalado' }[informe.value?.estado ?? 'borrador'])
)

const obligatorios = computed(() =>
  (informe.value?.redacciones ?? []).filter(r => ['analisis', 'conclusiones'].includes(r.bloque)).length
)
const redactados = computed(() =>
  (informe.value?.redacciones ?? []).filter(r => ['analisis', 'conclusiones'].includes(r.bloque) && r.contenido).length
)

function redaccion(idComponente: number, bloque: string): RedaccionInforme | undefined {
  return informe.value?.redacciones?.find(r => r.id_componente === idComponente && r.bloque === bloque)
}

function factorCompleto(idFactor: number): boolean {
  return !!redaccion(idFactor, 'conclusiones')?.contenido
}

const contexto = computed(() => {
  const s = seleccion.value
  if (!s || s.tipo === 'general' || s.tipo === 'libre') return ''
  for (const f of factores.value) {
    if (f.id === s.id) return `${f.nombre} — bloques de cierre del factor`
    const c = f.hijos.find(h => h.id === s.id)
    if (c) return `${f.nombre} → ${c.nombre} — análisis de la característica`
  }
  return ''
})

const contenidoActual = computed(() => {
  const s = seleccion.value
  if (!s || !informe.value) return null
  if (s.tipo === 'general') return (informe.value as any)[s.campo] as string | null
  if (s.tipo === 'libre') return getTextoLibre(s.id)
  const bloque = s.nivel === 'factor' ? bloqueActivo.value : 'analisis'
  if (s.nivel === 'factor' && esBloqueLibre(s.id, bloque)) return getTextoLibre(`comp-${s.id}-${bloque}`)
  return redaccion(s.id, bloque)?.contenido ?? null
})

// Editor Quill 

async function montarEditor(): Promise<void> {
  if (avalado.value) return
  await nextTick()
  const targetEl = bloqueEditandoId.value ? inlineEditorEl : editorEl.value
  if (!targetEl) return
  if (quill && quillMountedOn !== targetEl) {
    quill = null
    quillMountedOn = null
  }
  if (!quill) {
    quill = new Quill(targetEl, {
      theme: 'snow',
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline'],
          [{ header: [3, 4, false] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link', 'clean']
        ]
      }
    })
    quillMountedOn = targetEl
  }
  if (bloqueEditandoId.value) {
    const bloque = bloquesActuales.value.find(b => b.id === bloqueEditandoId.value)
    quill.root.innerHTML = bloque?.params?.html ?? ''
  } else {
    quill.root.innerHTML = contenidoActual.value ?? ''
  }
}

function seleccionarComponente(id: number, nivel: 'factor' | 'caracteristica'): void {
  seleccion.value = { tipo: 'componente', id, nivel }
  if (nivel === 'factor') {
    bloqueActivo.value = subSeccionesDeFactorVisibles(id)[0]?.clave ?? 'conclusiones'
  }
  montarEditor()
}

function cambiarBloque(clave: string): void {
  bloqueActivo.value = clave
  montarEditor()
}

// Guardar
async function guardar(): Promise<void> {
  const s = seleccion.value
  if (!s || !informe.value || !quill) return
  guardando.value = true
  const html = quill.root.innerHTML === '<p><br></p>' ? null : quill.root.innerHTML

  if (bloqueEditandoId.value) {
    const idx = bloquesActuales.value.findIndex(b => b.id === bloqueEditandoId.value)
    if (idx >= 0) {
      bloquesActuales.value[idx] = {
        ...bloquesActuales.value[idx],
        params: { ...bloquesActuales.value[idx].params, html: html ?? '' }
      }
      guardarBloques()
      toast('Guardado')
    }
    guardando.value = false
    return
  }

  if (s.tipo === 'libre') {
    setTextoLibre(s.id, html)
    toast('Guardado')
    guardando.value = false
    return
  }

  if (s.tipo === 'general') {
    const r = await actualizarInformeService(informe.value.id_informe, { [s.campo]: html } as Partial<Informe>)
    if (r.ok) { if (r.data) informe.value = r.data; toast('Guardado') }
    else toast(r.error ?? 'Error al guardar', 'error')
  } else if (s.tipo === 'componente') {
    const bloque = s.nivel === 'factor' ? bloqueActivo.value : 'analisis'
    if (s.nivel === 'factor' && esBloqueLibre(s.id, bloque)) {
      setTextoLibre(`comp-${s.id}-${bloque}`, html)
      toast('Guardado')
      guardando.value = false
      return
    }
    const r = await guardarRedaccionService(informe.value.id_informe, s.id, bloque, html)
    if (r.ok) {
      toast('Guardado')
      if (r.data) {
        const idx = informe.value.redacciones?.findIndex(x => x.id_redaccion === r.data!.id_redaccion) ?? -1
        if (idx >= 0 && informe.value.redacciones)
          informe.value.redacciones[idx] = { ...informe.value.redacciones[idx], ...r.data }
      }
    } else {
      toast(r.error ?? 'Error al guardar', 'error')
    }
  }
  guardando.value = false
}

// Acciones de informe 
async function generar(): Promise<void> {
  if (!tipoSeleccionado.value) return
  generando.value = true
  const r = await generarInformeService(idEjecucion, tipoSeleccionado.value)
  if (r.ok) await cargarInforme()
  else toast(r.error ?? 'No se pudo generar el informe', 'error')
  generando.value = false
}

async function avalar(): Promise<void> {
  if (!informe.value) return
  avalando.value = true
  const r = await avalarInformeService(informe.value.id_informe)
  if (r.ok) { await cargarInforme(); toast('Informe avalado correctamente') }
  else toast(r.error ?? 'No se pudo avalar', 'error')
  avalando.value = false
}

function descargarWord(): void {
  wordAlAbrir.value = true
  mostrarPreview.value = true
}

function descargarPdf(): void {
  imprimirAlAbrir.value = true
  mostrarPreview.value = true
}

async function cargarInforme(): Promise<void> {
  const r = await getInformeService(idEjecucion)
  if (r.ok && r.data) { informe.value = r.data; cargarEstructura() }
}

// Gestión de bloques (servidor + localStorage como respaldo)

function seccionKeyActual(): string | null {
  const s = seleccion.value
  if (!s) return null
  if (s.tipo === 'general') return `gen-${s.campo}`
  if (s.tipo === 'libre') return s.id
  if (s.nivel === 'factor') return `comp-${s.id}-${bloqueActivo.value}`
  return `comp-${s.id}-analisis`
}

function storageKey(skey: string): string {
  return `informe-bloques-${informe.value!.id_informe}-${skey}`
}

async function guardarBloquesEnServidor(): Promise<void> {
  if (!informe.value) return
  const s = seleccion.value
  if (!s) return
  const json = bloquesActuales.value.length > 0
    ? JSON.stringify(bloquesActuales.value)
    : null

  if (s.tipo === 'componente') {
    return
  } else {
    const clave = s.tipo === 'general' ? (s as any).campo : (s as any).id
    const secIdx = (informe.value.secciones ?? []).findIndex(sec => sec.clave === clave)
    if (secIdx < 0) return
    const secciones = informe.value.secciones.map((sec, i) =>
      i === secIdx ? { ...sec, contenido: json } : sec
    )
    const r = await actualizarInformeService(
      informe.value.id_informe, { secciones } as Partial<Informe>
    )
    if (r.ok && r.data) informe.value = r.data
  }
}

function recargarBloques(): void {
  const key = seccionKeyActual()
  if (!key || !informe.value) { bloquesActuales.value = []; return }
  const s = seleccion.value
  if (!s) { bloquesActuales.value = []; return }

  // Primero: datos del servidor (ya cargados en informe.value)
  let serverJson: string | null | undefined = null
  if (s.tipo === 'componente') {
    const bloqueBase = s.nivel === 'factor' ? bloqueActivo.value : 'analisis'
    serverJson = informe.value.redacciones?.find(
      r => r.id_componente === s.id && r.bloque === bloqueBase + '_bloques'
    )?.contenido
  } else {
    const clave = s.tipo === 'general' ? (s as any).campo : (s as any).id
    serverJson = (informe.value.secciones ?? []).find(sec => sec.clave === clave)?.contenido
  }

  if (serverJson) {
    try { bloquesActuales.value = JSON.parse(serverJson); return } catch { /* continuar */ }
  }

  // Respaldo: localStorage
  try {
    const raw = localStorage.getItem(storageKey(key))
    bloquesActuales.value = raw ? JSON.parse(raw) : []
  } catch { bloquesActuales.value = [] }
}

function guardarBloques(): void {
  const key = seccionKeyActual()
  if (!key || !informe.value) return
  localStorage.setItem(storageKey(key), JSON.stringify(bloquesActuales.value))
  guardarBloquesEnServidor().catch(() => toast('No se pudieron guardar los bloques', 'error'))
}

function agregarBloque(b: { tipo: string; params?: Record<string, any> }): void {
  const params = b.tipo === 'COMPARATIVO'
    ? { ...b.params, id_ejecucion: idEjecucion }
    : b.params
  const nuevoId = crypto.randomUUID()
  bloquesActuales.value.push({ id: nuevoId, tipo: b.tipo, params })
  guardarBloques()
  mostrarCatalogo.value = false
  if (b.tipo === 'TEXTO') nextTick(() => editarBloqueTexto(nuevoId))
}

function actualizarParams(payload: { id: string; params: Record<string, any> }): void {
  const idx = bloquesActuales.value.findIndex(b => b.id === payload.id)
  if (idx >= 0) bloquesActuales.value[idx] = { ...bloquesActuales.value[idx], params: payload.params }
  guardarBloques()
  bloqueConfigurando.value = null
}

function actualizarVistaComparativo(id: string, vista: 'tabla' | 'grafico'): void {
  const idx = bloquesActuales.value.findIndex(b => b.id === id)
  if (idx >= 0) {
    bloquesActuales.value[idx] = {
      ...bloquesActuales.value[idx],
      params: { ...bloquesActuales.value[idx].params, vista }
    }
    guardarBloques()
  }
}

function eliminarBloque(id: string): void {
  bloquesActuales.value = bloquesActuales.value.filter(b => b.id !== id)
  guardarBloques()
}

function moverBloque(idx: number, dir: -1 | 1): void {
  const arr = [...bloquesActuales.value]
  const nIdx = idx + dir
  if (nIdx < 0 || nIdx >= arr.length) return
  ;[arr[idx], arr[nIdx]] = [arr[nIdx], arr[idx]]
  bloquesActuales.value = arr
  guardarBloques()
}

function editarBloqueTexto(id: string): void {
  bloqueEditandoId.value = id
  montarEditor()
}

function cancelarEdicionBloque(): void {
  bloqueEditandoId.value = null
  montarEditor()
}

function eliminarBloqueConReset(id: string): void {
  if (bloqueEditandoId.value === id) {
    bloqueEditandoId.value = null
    nextTick(() => montarEditor())
  }
  eliminarBloque(id)
}

watch([seleccion, bloqueActivo], () => {
  bloqueEditandoId.value = null
  recargarBloques()
})

// Montaje 

onMounted(async () => {
  const [rTipos, rInforme, , proceso, tiposEjecucion] = await Promise.all([
    getTiposInformeService(),
    getInformeService(idEjecucion),
    resultadosStore.cargar(idEjecucion),
    getProcesoByIdService(idEjecucion),
    getTiposEjecucionModeloService()
  ])
  nombreVersion.value = (proceso as any)?.version?.nombre ?? ''
  if (rTipos.ok && rTipos.data)     tipos.value   = rTipos.data
  if (rInforme.ok && rInforme.data) { informe.value = rInforme.data; cargarEstructura() }
  if (!informe.value && tipos.value.length && proceso) {
    const norm = (s: string) =>
      s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[-_\s]/g, '').toLowerCase()
    const tipoStr = proceso.tipoProceso?.tipo
      ?? tiposEjecucion.find((t: any) => t.id_tipo_ejecucion_modelo === proceso.id_tipo_ejecucion_modelo)?.tipo
    if (tipoStr) {
      const key = norm(tipoStr)
      const match = tipos.value.find(t => norm(t.nombre) === key || norm(t.etiqueta) === key)
      if (match) {
        tipoSeleccionado.value = match.id_tipo_informe
        tipoAutoDetectado.value = true
      }
    }
  }
    if (resultadosStore.resultados) {
    factores.value = (resultadosStore.resultados.factores ?? []).map((f: any) => ({
      id:     f.id_componente,
      nombre: f.nombre,
      hijos:  (f.hijos ?? []).map((c: any) => ({ id: c.id_componente, nombre: c.nombre }))
    }))
  }
  cargando.value = false
})
</script>

<style scoped>
.formulario-modelo {
  background-color: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  flex: 1; min-height: 0; overflow-y: auto;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px;
  font-family: var(--font-family);
  display: flex; flex-direction: column;
}

h1 { font-size: 1.4rem; font-weight: 700; margin: 0; color: var(--text-primary); }
.ctx-modelo { display: inline-block; margin: 6px 0 0; padding: 3px 10px; background: #e8f0f8; border-left: 3px solid #0e69a0; border-radius: 3px; font-size: 0.85rem; font-weight: 700; color: #111; letter-spacing: 0.01em; }
.header-flex { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; }
.subtitulo { font-size: 0.85rem; color: #666; margin: 0.25rem 0 0; }
.divider { border: none; border-bottom: 1px solid #e0e0e0; margin: 12px 0 16px; }
.info { padding: 1rem 0; color: #555; font-size: 0.9rem; }

.selector-tipo { max-width: 600px; }
.ayuda { font-size: 0.9rem; color: #555; margin: 0 0 1rem; }
.tipos { display: grid; gap: 8px; margin-bottom: 1.25rem; }
.tipo-card {
  display: flex; align-items: center; gap: 12px;
  border: 2px solid #e0e0e0; border-radius: var(--radius-md);
  padding: 12px 16px; cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.tipo-card:hover { border-color: #0e69a0; }
.tipo-card.activo { border-color: #0e69a0; background: #e8f4fd; }
.tipo-card input { accent-color: #0e69a0; width: 16px; height: 16px; flex-shrink: 0; }
.tipo-card--oculto { display: none; }

.bloque-editor-inline { margin-top: 6px; }
.bloque-colapsado-label {
  padding: 6px 10px;
  font-size: 0.8rem;
  color: #666;
  background: #f5f5f5;
  border-radius: 4px;
  margin-top: 4px;
}
.tipo-nombre { display: block; font-weight: 600; font-size: 0.95rem; }
.tipo-detalle { display: block; font-size: 0.8rem; color: #666; margin-top: 2px; }
.botones-generar { display: flex; }

.barra-estado {
  display: flex; justify-content: space-between; align-items: center;
  flex-wrap: wrap; gap: 0.75rem; padding: 0.75rem 1rem;
  background: #f8f9fa; border: 1px solid #e0e0e0;
  border-radius: var(--radius-md); margin-bottom: 1rem;
}
.barra-izq { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
.barra-der { display: flex; align-items: center; gap: 0.5rem; }
.badge-estado {
  display: inline-block; padding: 4px 12px; border-radius: 6px;
  font-size: 0.7rem; font-weight: 800; letter-spacing: 0.1em;
  text-transform: uppercase; color: white; box-shadow: 0 2px 4px rgba(0,0,0,0.15);
}
.badge-estado.borrador    { background: linear-gradient(135deg, #c47a00 0%, #eea222 100%); }
.badge-estado.en_revision { background: linear-gradient(135deg, #0e69a0 0%, #1a8dcf 100%); }
.badge-estado.avalado     { background: linear-gradient(135deg, #1a7f1a 0%, #28a745 100%); }
.progreso-texto { font-size: 0.88rem; color: #555; }
.progreso-texto strong { color: #0e69a0; }

.panel-doble {
  display: grid; grid-template-columns: 270px 1fr;
  gap: 1rem; flex: 1; min-height: 0; align-items: start;
}
.panel-doble.panel-sin-arbol {
  grid-template-columns: 1fr;
}

.arbol {
  border: 1px solid #e0e0e0; border-radius: var(--radius-md);
  background: #fafafa; overflow-y: auto; max-height: 65vh;
}

.arbol-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 10px 6px; border-bottom: 1px solid #e8e8e8;
}
.arbol-head-label {
  font-size: 0.68rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.08em; color: #999;
}
.btn-modo-edicion {
  font-size: 0.72rem; font-weight: 700; padding: 3px 10px;
  border: 1px solid #ccc; border-radius: 5px; background: white;
  color: #555; cursor: pointer; font-family: inherit; transition: all 0.15s;
}
.btn-modo-edicion:hover { border-color: #0e69a0; color: #0e69a0; }
.btn-modo-edicion.activo { background: #0e69a0; border-color: #0e69a0; color: white; }

.arbol-separador {
  font-size: 0.68rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.06em; color: #999;
  padding: 10px 10px 4px; border-top: 1px solid #e8e8e8; margin-top: 4px;
}

.nodo {
  padding: 5px 10px; border-radius: 6px; cursor: pointer;
  font-size: 0.84rem; line-height: 1.4;
  transition: background-color 0.1s ease, color 0.1s ease;
  margin: 1px 6px;
}
.nodo:hover:not(.activo) { background: #e8f4fd; }
.nodo.activo { background: #d0e8f5; color: #0e69a0; font-weight: 600; box-shadow: inset 0 0 0 1px #b0d4ec; }
.nodo.activo .check { color: #28a745; }
.nodo.general  { font-style: italic; color: #444; }
.nodo.libre    { color: #555; }
.nodo.general.activo, .nodo.libre.activo { color: #0e69a0; }
.nodo.factor   { font-weight: 700; margin-top: 6px; color: #1a1a1a; }
.nodo.factor.activo { color: #0e69a0; }
.nodo.caracteristica { padding-left: 22px; color: #555; }
.check { color: #28a745; margin-right: 5px; font-size: 0.72rem; }

.edit-grupo-label {
  font-size: 0.68rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.08em; color: #0e69a0;
  padding: 8px 10px 4px;
}
.edit-factor-label {
  font-size: 0.78rem; font-weight: 700; color: #1a1a1a;
  padding: 8px 10px 3px; margin-top: 4px;
  border-top: 1px solid #e8e8e8;
}
.edit-item {
  display: flex; align-items: center; gap: 6px;
  padding: 4px 8px; margin: 1px 4px; border-radius: 5px;
  background: white; border: 1px solid #e8e8e8; transition: opacity 0.15s;
}
.edit-item.sub { margin-left: 12px; }
.edit-item.oculta { opacity: 0.45; }
.edit-toggle {
  font-size: 0.65rem; font-weight: 700; padding: 2px 7px; flex-shrink: 0;
  border-radius: 4px; border: 1px solid #ddd; cursor: pointer;
  background: #f0f0f0; color: #888; font-family: inherit; transition: all 0.15s;
}
.edit-toggle.visible { background: #e8f4fd; border-color: #0e69a0; color: #0e69a0; }
.edit-titulo {
  flex: 1; font-size: 0.82rem; color: #333;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.edit-ctrl { display: flex; gap: 3px; flex-shrink: 0; }

.nueva-sec-form {
  display: flex; gap: 5px; align-items: center;
  padding: 6px 8px; margin: 4px 4px 0;
}
.nueva-sec-form.sub { margin-left: 16px; }
.form-control-mini {
  flex: 1; min-width: 0; font-size: 0.78rem; padding: 4px 8px;
  border: 1px solid #ccc; border-radius: 5px; font-family: inherit;
}
.form-control-mini:focus { outline: none; border-color: #0e69a0; }
.btn-add-sec {
  font-size: 0.75rem; font-weight: 600; padding: 4px 10px; flex-shrink: 0;
  border: 1px solid #0e69a0; border-radius: 5px;
  background: #0e69a0; color: white; cursor: pointer; font-family: inherit;
  transition: background 0.15s;
}
.btn-add-sec:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-add-sec:hover:not(:disabled) { background: #0b5580; }
.btn-cancel {
  font-size: 0.75rem; padding: 4px 8px; flex-shrink: 0;
  border: 1px solid #ccc; border-radius: 5px;
  background: white; color: #555; cursor: pointer; font-family: inherit;
}
.btn-add-subsec {
  font-size: 0.72rem; font-weight: 600; padding: 3px 10px;
  border: 1px dashed #c0d0dc; border-radius: 5px;
  background: transparent; color: #0e69a0; cursor: pointer;
  font-family: inherit; margin: 3px 12px 4px; display: block;
  transition: all 0.15s;
}
.btn-add-subsec:hover { background: #eaf3fb; border-color: #0e69a0; }

.editor-panel {
  border: 1px solid #e0e0e0; border-radius: var(--radius-md);
  padding: 1rem; background: white; display: flex; flex-direction: column; min-height: 420px;
  min-width: 0;
}
.tabs {
  display: flex; gap: 4px; flex-wrap: wrap;
  margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #e0e0e0;
}
.tab {
  border: 1px solid #d0d0d0; background: #f5f5f5; border-radius: 6px;
  padding: 5px 12px; font-size: 0.8rem; cursor: pointer;
  font-family: var(--font-family); transition: all 0.15s ease;
}
.tab:hover:not(.activo) { border-color: #0e69a0; color: #0e69a0; background: #e8f4fd; }
.tab.activo { background: #0e69a0; border-color: #0e69a0; color: white; font-weight: 600; }

.contexto {
  font-size: 0.82rem; color: #555; margin: 0 0 12px;
  padding: 6px 10px; background: #f0f8ff; border-radius: 4px; border-left: 3px solid #0e69a0;
}
.editor { flex: 1; min-height: 260px; }
.editor-pie {
  display: flex; justify-content: flex-end; align-items: center;
  gap: 12px; margin-top: 12px; padding-top: 12px; border-top: 1px solid #e0e0e0;
}
.solo-lectura {
  flex: 1; padding: 12px 14px; background: #fafafa;
  border-radius: 6px; border: 1px solid #e8e8e8;
  font-size: 0.9rem; line-height: 1.7; color: #333;
}
.vacio {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center; color: #999; gap: 0.5rem;
}
.vacio p { font-size: 0.9rem; margin: 0; text-align: center; }
.error-global { color: #dc3545; font-size: 0.9rem; margin-top: 1rem; }

.btn {
  padding: 0.55rem 1.1rem; font-weight: 600; border: none;
  border-radius: 8px; cursor: pointer; transition: all 0.15s ease;
  font-family: var(--font-family); font-size: 0.85rem;
  display: inline-flex; align-items: center; white-space: nowrap;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn.guardar { background-color: #eea222; color: black; }
.btn.guardar:hover:not(:disabled) { background-color: #0e69a0; color: white; }
.btn.salir { background-color: white; border: 1px solid #ccc; color: #333; }
.btn.salir:hover:not(:disabled) { background-color: #d41a1a; color: white; }
.btn.accion { background-color: #0e69a0; color: white; }
.btn.accion:hover:not(:disabled) { background-color: #0b5580; }
.btn-toggle-arbol {
  background: white; border: 1px solid #c6ddf7; color: #0e69a0;
  font-size: 0.78rem; padding: 0.4rem 0.75rem;
}
.btn-toggle-arbol:hover { background: #e8f4fd; border-color: #0e69a0; }

.bloques-area {
  border: 1px dashed #c0d8ea; border-radius: var(--radius-md);
  padding: 0.75rem; margin-bottom: 0.75rem; background: #f8fbfe;
}
.bloques-header {
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem;
}
.bloques-label {
  font-size: 0.75rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.06em; color: #0e69a0;
}
.btn-insertar {
  font-size: 0.8rem; font-weight: 600; padding: 4px 12px;
  border: 1px solid #0e69a0; border-radius: 6px;
  background: white; color: #0e69a0; cursor: pointer;
  font-family: var(--font-family); transition: all 0.15s;
}
.btn-insertar:hover { background: #0e69a0; color: white; }
.bloques-lista { display: flex; flex-direction: column; gap: 0.75rem; }
.bloque-item { border: 1px solid #dce8f0; border-radius: 6px; background: white; overflow: hidden; }
.bloque-controles {
  display: flex; align-items: center; justify-content: space-between;
  padding: 5px 8px; background: #eaf3fb; border-bottom: 1px solid #dce8f0;
}
.bloque-tipo-label {
  font-size: 0.7rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.05em; color: #0e69a0;
}
.bloque-acciones { display: flex; gap: 4px; }
.btn-ctrl {
  border: 1px solid #c0d0dc; background: white; border-radius: 4px;
  width: 24px; height: 24px; font-size: 0.7rem; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.1s; color: #555;
}
.btn-ctrl:hover:not(:disabled) { border-color: #0e69a0; color: #0e69a0; background: #eaf3fb; }
.btn-ctrl:disabled { opacity: 0.35; cursor: not-allowed; }
.btn-ctrl.eliminar:hover:not(:disabled) { border-color: #dc3545; color: #dc3545; background: #fff5f5; }
.btn-ctrl.opciones { width: auto; padding: 0 8px; font-size: 0.68rem; color: #0e69a0; border-color: #b0d0e8; }
.btn-ctrl.opciones:hover { background: #eaf3fb; border-color: #0e69a0; }
.btn-ctrl--texto { width: auto; padding: 0 8px; font-size: 0.68rem; white-space: nowrap; }

.bloque-texto-preview { border-radius: var(--radius-sm); transition: outline 0.1s; }
.bloque-texto-preview--editable { cursor: pointer; }
.bloque-texto-preview--editable:hover { outline: 2px dashed #0e69a0; outline-offset: 2px; }
:deep(.bloque-item > .bloque-datos) { padding: 0.75rem; }
.bloques-vacio {
  font-size: 0.78rem; color: #aaa; font-style: italic;
  margin: 0; text-align: center; padding: 0.3rem 0;
}
.bloque-editando { border-color: #eea222; box-shadow: 0 0 0 2px rgba(238,162,34,0.25); }
.bloque-editando .bloque-controles { background: #fef8ec; border-bottom-color: #f5d580; }

.editor-cabecera {
  display: flex; align-items: center; margin-bottom: 6px;
}
.editor-contexto-label {
  font-size: 0.75rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.05em; color: #0e69a0;
  display: flex; align-items: center; gap: 8px;
}
.btn-cancelar-edicion {
  font-size: 0.72rem; font-weight: 600; padding: 2px 8px;
  border: 1px solid #0e69a0; border-radius: 4px;
  background: white; color: #0e69a0; cursor: pointer;
  font-family: var(--font-family); transition: all 0.12s;
}
.btn-cancelar-edicion:hover { background: #0e69a0; color: white; }
</style>
